import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { NewsletterTemplate } from "./types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { TemplateBasicInfo } from "./editor/TemplateBasicInfo";
import { TemplateVariables } from "./editor/TemplateVariables";
import { TemplateContent } from "./editor/TemplateContent";
import { TemplatePrivacySettings } from "./editor/TemplatePrivacySettings";
import { PREDEFINED_VARIABLES } from "./constants";

interface EditTemplateDialogProps {
  template: NewsletterTemplate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditTemplateDialog = ({ template, open, onOpenChange }: EditTemplateDialogProps) => {
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description || "");
  const [templateType, setTemplateType] = useState(template.template_type);
  const [content, setContent] = useState(() => {
    if (!template.content_structure) return "";
    try {
      if (typeof template.content_structure === 'string') {
        return template.content_structure;
      }
      if (typeof template.content_structure === 'object') {
        if ('template' in template.content_structure) {
          const templateContent = template.content_structure.template;
          return typeof templateContent === 'string' ? templateContent : JSON.stringify(templateContent, null, 2);
        }
        return JSON.stringify(template.content_structure, null, 2);
      }
      return String(template.content_structure) || "";
    } catch (error) {
      console.error('Error initializing content:', error);
      return String(template.content_structure) || "";
    }
  });
  
  const [includePrivacyNotice, setIncludePrivacyNotice] = useState(template.include_privacy_notice || false);
  const [privacyNoticePosition, setPrivacyNoticePosition] = useState(template.privacy_notice_position || 'footer');
  const [customPrivacyText, setCustomPrivacyText] = useState(template.custom_privacy_text || '');
  const [selectedCategory, setSelectedCategory] = useState<keyof typeof PREDEFINED_VARIABLES>("USER");
  const queryClient = useQueryClient();

  const handleInsertVariable = (variable: string) => {
    const variableText = `{${variable}}`;
    setContent((prev) => prev + variableText);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const contentStructure = {
        template: content,
        variables: template.content_structure?.variables || {}
      };

      const { error } = await supabase
        .from("newsletter_templates")
        .update({
          name,
          description,
          template_type: templateType,
          content_structure: contentStructure,
          include_privacy_notice: includePrivacyNotice,
          privacy_notice_position: privacyNoticePosition,
          custom_privacy_text: customPrivacyText
        })
        .eq("id", template.id);

      if (error) throw error;

      toast.success("Template updated successfully");
      queryClient.invalidateQueries({ queryKey: ["newsletter-templates"] });
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error("Failed to update template");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Edit Template</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Tabs defaultValue="content" className="w-full">
            <TabsList>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
              <TabsTrigger value="variables">Variables</TabsTrigger>
              <TabsTrigger value="privacy">Privacy</TabsTrigger>
            </TabsList>

            <TabsContent value="content">
              <TemplateContent 
                content={content}
                onChange={setContent}
              />
            </TabsContent>

            <TabsContent value="settings">
              <TemplateBasicInfo
                name={name}
                setName={setName}
                description={description}
                setDescription={setDescription}
                templateType={templateType}
                setTemplateType={setTemplateType}
              />
            </TabsContent>

            <TabsContent value="variables">
              <TemplateVariables
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                content={content}
                setContent={setContent}
                handleInsertVariable={handleInsertVariable}
              />
            </TabsContent>

            <TabsContent value="privacy">
              <TemplatePrivacySettings
                includePrivacyNotice={includePrivacyNotice}
                privacyNoticePosition={privacyNoticePosition}
                customPrivacyText={customPrivacyText}
                defaultPrivacyText={template.default_privacy_text || ''}
                onIncludePrivacyNoticeChange={setIncludePrivacyNotice}
                onPrivacyNoticePositionChange={setPrivacyNoticePosition}
                onCustomPrivacyTextChange={setCustomPrivacyText}
              />
            </TabsContent>
          </Tabs>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};